import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  AlertTriangle,
  CheckCircle, ArrowRight,
  DollarSign,
  RefreshCcw,
  Clock, Check,
  X, Pause, Lock, Handshake
} from 'lucide-react';
import CCFSCalculator from '@/components/CCFSCalculator';
import FloatingShare from '@/components/FloatingShare';

import { SEO } from '@/components/SEO';

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/f37f0f12-f03c-4092-a88a-c8dade840c1e/images/6d0f9b629e532b160e203e85d8f217359fccdc0656859decc1dfeea7d633eb65.png";

export default function CCFSv2Page() {
  // Scroll helper that accounts for fixed navbar
  const scrollToElement = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        const offset = 100;
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 150);
  };

  const scrollToTool = () => {
    scrollToElement('tool');
  };

  return (
    <div className="pt-20 bg-white" data-testid="ccfs-v2-page">
      <FloatingShare
        path="/ccfs-2026-company-status-check"
        title="CCFS 2026 — Free Company Status Check"
        description="Running an inactive company? Check if you should close, restart, or make it dormant — and see how much you can save under the CCFS-2026 scheme. Free 3-minute assessment by Ratnatrraya Consulting."
      />
      <SEO 
        title="CCFS 2026 Company Status Check - Close, Restart or Make Dormant"
        path="/ccfs-2026-company-status-check"
        description="Free 3-minute assessment tool for inactive companies under CCFS-2026. Find out if you should close, restart, or make your company dormant. Expert guidance from Ratnatrraya Consulting."
        keywords="CCFS 2026, inactive company India, company closure, dormant company, strike off company, MCA compliance, ROC filing, company reset"
      />
      {/* CCFS Urgency Ribbon */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2.5 px-4 animate-slideDown">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <Clock className="w-4 h-4 shrink-0 hidden sm:block animate-clock" />
          <p className="text-sm font-medium">
            <span className="hidden sm:inline">CCFS-2026 relief window open until <span className="bg-white text-orange-600 px-2 py-0.5 rounded font-bold mx-1">15 July 2026</span>. Companies with pending filings may save <strong>₹50,000 to ₹20,00,000+</strong> depending on delay and filings pending.</span>
            <span className="sm:hidden">CCFS-2026 open till <span className="bg-white text-orange-600 px-1.5 py-0.5 rounded font-bold">15 July 2026</span> – save ₹50K–₹20L+</span>
          </p>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-secondary text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                Sometimes the smartest move in business is to{' '}
                <span className="text-[#E84C3D]">close a chapter.</span>
              </h1>
              <p className="text-slate-400 text-base mb-4">
                Running an inactive company? This is a good time to reset it.
              </p>
              <p className="text-lg md:text-xl text-slate-200 mb-5">
                Close, pause, or restart your company — with clarity, compliance, and significant cost savings under CCF Scheme 2026.
              </p>
              <ul className="space-y-2.5 mb-6">
                <li className="flex items-start gap-2.5 text-slate-300 text-sm md:text-base">
                  <span className="text-[#E84C3D] mt-1 shrink-0">&#x2022;</span>
                  <span>See how much you can save on ROC penalties if you act now</span>
                </li>
                <li className="flex items-start gap-2.5 text-slate-300 text-sm md:text-base">
                  <span className="text-[#E84C3D] mt-1 shrink-0">&#x2022;</span>
                  <span>Get a simple step-by-step plan instead of confusing compliance rules</span>
                </li>
              </ul>
              <Button onClick={scrollToTool} className="bg-[#E84C3D] hover:bg-[#C0392B] px-6 sm:px-8 py-6 text-base sm:text-lg w-full sm:w-auto mb-5" data-testid="start-check-btn">
                Check Your Savings Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                <p className="text-amber-400 text-sm font-semibold flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" /> Save ₹50,000 to ₹20,00,000+ in penalties under CCFS 2026
                </p>
                <p className="text-amber-300/80 text-xs flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Actual savings depend on years of non-filing and delay period.
                </p>
                <p className="text-slate-400 text-xs flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500" /> Used by 200+ inactive companies to evaluate closure and savings options
                </p>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src={HERO_IMAGE} alt="Founder at crossroads" className="w-full max-w-lg mx-auto rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1 - Three Simple Paths */}
      <section className="py-16 md:py-20 bg-white" id="three-paths">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
              What should you do with your inactive company?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Depending on your situation, you typically have 3 practical options.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Close */}
            <Card className="border border-slate-200 hover:border-red-200 hover:shadow-lg transition-all h-full">
              <CardContent className="p-6">
                <div className="w-14 h-14 mb-4 rounded-2xl bg-red-100 flex items-center justify-center">
                  <X className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Close the Company</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• Best if business is inactive and not restarting</li>
                  <li>• Clear filings at reduced penalty and close at lower cost</li>
                </ul>
              </CardContent>
            </Card>
            
            {/* Restart */}
            <Card className="border border-slate-200 hover:border-green-200 hover:shadow-lg transition-all h-full">
              <CardContent className="p-6">
                <div className="w-14 h-14 mb-4 rounded-2xl bg-green-100 flex items-center justify-center">
                  <RefreshCcw className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Restart Operations</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• Continue using same company after clearing filings</li>
                  <li>• Pay normal fee + reduced late fee under scheme</li>
                </ul>
              </CardContent>
            </Card>
            
            {/* Dormant */}
            <Card className="border border-slate-200 hover:border-amber-200 hover:shadow-lg transition-all h-full">
              <CardContent className="p-6">
                <div className="w-14 h-14 mb-4 rounded-2xl bg-amber-100 flex items-center justify-center">
                  <Pause className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Keep it Dormant</h3>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li>• Keep company alive for future use</li>
                  <li>• Lower compliance and reduced government fees</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10 space-y-4">
            <div>
              <p className="text-slate-800 text-base font-medium">Confused whether to close, restart or pause?</p>
              <p className="text-slate-500 text-sm">Get a clear recommendation in 30 seconds based on your situation.</p>
            </div>
            <Button onClick={scrollToTool} className="bg-[#E84C3D] hover:bg-[#C0392B] px-6 sm:px-8 py-5 text-base">
              Check Your Savings Now <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Company Status Check Tool */}
      <section className="py-16 md:py-20 bg-white" id="tool">
        <div className="max-w-6xl mx-auto px-6 md:px-12">

          {/* CCFS-2026 Savings Calculator */}
          <CCFSCalculator />


          {/* 3-Step Progress Bar */}
        </div>
      </section>

      {/* TRUST & COMPLEX CASE HANDLING */}
      <section className="py-16 md:py-20 bg-white" data-testid="trust-complex-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Is your case complicated? We've handled worse.
            </h2>
            <p className="text-slate-600 text-base max-w-2xl mx-auto">
              Most inactive companies have at least 2-3 of these issues. Our team resolves them end-to-end.
            </p>
          </div>

          <div className="space-y-3 max-w-3xl mx-auto mb-10">
            {[
              { problem: 'Directors or shareholders unreachable or uncooperative', solution: 'We manage outreach, consent mechanisms, and legal course of action (if required)' },
              { problem: 'Pending tax demands or notices (Income Tax, GST, ROC)', solution: 'Our tax experts negotiate and resolve before you proceed' },
              { problem: 'Bank loans, NPA accounts, or banks not cooperating', solution: 'We work with banks and RBI frameworks to clear the path' },
              { problem: 'Investor approvals required (VC / Angel with board rights)', solution: 'Our team handles board consent, SHA clauses, and exits' },
              { problem: 'Unsettled creditors, employees, or vendor disputes', solution: 'Expert negotiators close outstanding claims' },
              { problem: 'Missing books of accounts or incomplete records', solution: 'We reconstruct from bank statements, IT returns, and MCA filings' },
              { problem: 'Inactive bank accounts or missing statements', solution: 'We coordinate directly with banks to retrieve records' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-lg p-4">
                <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-900">{item.problem}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Team credibility line */}
          <p className="text-center text-slate-700 text-sm font-medium mb-8 max-w-3xl mx-auto">
            Our team includes Chartered Accountants, Company Secretaries, tax advisors, lawyers, retired senior bank officers, and investment bankers — so you don't need to coordinate with anyone else.
          </p>

          {/* CTA */}
          <div className="text-center">
            <Button onClick={() => window.location.href = '/contact'} className="bg-[#E84C3D] hover:bg-[#C0392B] text-sm py-5 px-8" data-testid="trust-section-cta">
              Talk to an Expert About My Case <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="text-slate-400 text-xs mt-3">Confidential discussion &bull; No pressure to proceed</p>
          </div>
        </div>
      </section>

      {/* SECTION - For CAs, CS and Professional Firms */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="partner-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center">
                <Handshake className="w-6 h-6 text-[#E84C3D]" />
              </div>
              <h2 className="font-secondary text-xl md:text-2xl font-semibold text-slate-900">
                For CAs, CS and Professional Firms
              </h2>
            </div>
            <p className="text-slate-600 mb-4">
              Handling inactive companies, pending filings, or closure decisions for clients? Partner with Ratnatrraya to execute these cases faster — including complex situations involving banks, investors, or missing records.
            </p>
            <ul className="space-y-2 text-slate-600 text-sm mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Backend execution support
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Co-handling complex cases
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                Faster client resolution
              </li>
            </ul>
            <Link to="/contact">
              <Button className="bg-[#E84C3D] hover:bg-[#C0392B]" data-testid="partner-btn">
                Partner With Ratnatrraya <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION - What founders actually get */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900">
              What founders actually get
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              'Clear recommendation — close, restart, or dormant',
              'End-to-end execution (not just advice)',
              'Handling of banks, investors, and compliance issues',
              'Transparent cost visibility before you begin',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                <p className="text-slate-700 text-sm font-medium">{item}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-400 text-xs mt-6">Confidential and end-to-end managed</p>
        </div>
      </section>

      {/* SECTION - FAQ */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-secondary text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                q: "What is CCFS-2026?",
                a: "CCFS-2026 allows companies to clear pending ROC filings at significantly reduced penalties and close or pause companies at lower cost during a limited-time window."
              },
              {
                q: "Is this tool really free?",
                a: "Yes. The self-check and the first clarity call are free. You only pay professional fees if you decide to go ahead with closure, dormant or regularisation work."
              },
              {
                q: "Does this tool give a final legal opinion?",
                a: "No. It's a starting point in plain language. Final advice depends on reviewing your documents and applying the CCFS-2026 rules to your specific case."
              },
              {
                q: "What if I decide not to close after using the tool?",
                a: "That's perfectly fine. Even if you only use CCFS-2026 to clear pending filings and keep the company active, you still benefit from lower late fees during the scheme."
              },
              {
                q: "Can I share this with my CA/CS or co-founder?",
                a: "Yes. Many founders do the check with their CA/CS, and we often work together as a combined team."
              },
              {
                q: "Will using CCFS trigger scrutiny or penalties later?",
                a: "No. CCFS-2026 is designed to encourage voluntary compliance and closure of inactive companies. Using the scheme does not automatically trigger penalties or scrutiny."
              }
            ].map((faq, i) => (
              <Card key={i} className="bg-white">
                <CardContent className="p-5">
                  <h4 className="font-semibold text-slate-900 mb-2">{faq.q}</h4>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-2xl md:text-3xl font-semibold mb-4">
            Ready to see your options?
          </h2>
          <p className="text-slate-400 mb-4 text-lg">
            Start the free Company Reset Check or book a call directly.
          </p>
          <p className="text-amber-400 text-sm font-medium mb-8">
            CCFS-2026 window closes soon — delaying may increase your cost significantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={scrollToTool} className="bg-[#E84C3D] hover:bg-[#C0392B] px-6 sm:px-8 py-6 text-base sm:text-lg w-full sm:w-auto">
              Start Company Reset Check <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-6 sm:px-8 py-6 text-base sm:text-lg w-full sm:w-auto">
                Book Consultation
              </Button>
            </Link>
          </div>
          <p className="text-slate-500 text-sm mt-6">Free initial check &bull; Confidential &bull; No obligation</p>
        </div>
      </section>
    </div>
  );
}
